/**
 * Cubic Hermite interpolation on irregular time grids, using positions and velocities (state vectors).
 * Samples are stored flat: `s[6i..6i+5] = x, y, z, vx, vy, vz` with velocity in position units per time unit.
 */
import type { Vec3 } from './vec3';

/**
 * Index `i` with `t[i] ≤ x ≤ t[i+1]` (clamped to the valid range). `hint` (e.g. the previous result) makes
 * sequential playback O(1).
 */
export function findInterval(t: ArrayLike<number>, x: number, hint = 0): number {
  const n = t.length;
  if (n < 2) return 0;
  if (hint >= 0 && hint < n - 1) {
    if (t[hint]! <= x && x <= t[hint + 1]!) return hint;
    if (hint + 2 < n && t[hint + 1]! <= x && x <= t[hint + 2]!) return hint + 1;
  }
  if (x <= t[0]!) return 0;
  if (x >= t[n - 1]!) return n - 2;
  let lo = 0;
  let hi = n - 1;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (t[mid]! <= x) lo = mid;
    else hi = mid;
  }
  return lo;
}

/**
 * Evaluates position (and optionally velocity) at `x` on interval `i`. Writes into `pos`/`vel`.
 */
export function hermiteAt(
  t: ArrayLike<number>,
  s: ArrayLike<number>,
  i: number,
  x: number,
  pos: Vec3,
  vel?: Vec3,
): void {
  const t0 = t[i]!;
  const h = t[i + 1]! - t0;
  const u = (x - t0) / h;
  const u2 = u * u;
  const u3 = u2 * u;
  const h00 = 2 * u3 - 3 * u2 + 1;
  const h10 = (u3 - 2 * u2 + u) * h;
  const h01 = -2 * u3 + 3 * u2;
  const h11 = (u3 - u2) * h;
  const a = 6 * i;
  const b = a + 6;
  pos.x = h00 * s[a]! + h10 * s[a + 3]! + h01 * s[b]! + h11 * s[b + 3]!;
  pos.y = h00 * s[a + 1]! + h10 * s[a + 4]! + h01 * s[b + 1]! + h11 * s[b + 4]!;
  pos.z = h00 * s[a + 2]! + h10 * s[a + 5]! + h01 * s[b + 2]! + h11 * s[b + 5]!;
  if (vel) {
    const d00 = (6 * u2 - 6 * u) / h;
    const d10 = 3 * u2 - 4 * u + 1;
    const d01 = (-6 * u2 + 6 * u) / h;
    const d11 = 3 * u2 - 2 * u;
    vel.x = d00 * s[a]! + d10 * s[a + 3]! + d01 * s[b]! + d11 * s[b + 3]!;
    vel.y = d00 * s[a + 1]! + d10 * s[a + 4]! + d01 * s[b + 1]! + d11 * s[b + 4]!;
    vel.z = d00 * s[a + 2]! + d10 * s[a + 5]! + d01 * s[b + 2]! + d11 * s[b + 5]!;
  }
}
